import type { CssRect, ScreenCorners } from './types'

/**
 * Pure port of the framebuffer-to-CSS rectangle conversion performed inside
 * `ChartView.updatePosition()`. Kept isolated so the harness and its
 * deterministic tests exercise the exact same math.
 *
 * Returns `undefined` when the inputs cannot produce a valid, positive-size
 * rectangle (matching `ChartView`'s early-return behavior).
 */
export function computePlaceholderRect(
  canvasRect: { left: number; top: number; width: number; height: number },
  resolutionX: number,
  resolutionY: number,
  corners: ScreenCorners | undefined
): CssRect | undefined {
  if (!canvasRect.width || !canvasRect.height) {
    return undefined
  }

  if (!resolutionX || !resolutionY) {
    return undefined
  }

  if (!corners || corners.length < 4) {
    return undefined
  }

  const leftNormalized = corners[0].x / resolutionX
  const rightNormalized = corners[2].x / resolutionX
  const topNormalized = 1 - corners[2].y / resolutionY
  const bottomNormalized = 1 - corners[0].y / resolutionY

  const left = canvasRect.left + leftNormalized * canvasRect.width
  const right = canvasRect.left + rightNormalized * canvasRect.width
  const top = canvasRect.top + topNormalized * canvasRect.height
  const bottom = canvasRect.top + bottomNormalized * canvasRect.height

  const width = right - left
  const height = bottom - top

  if (width <= 0 || height <= 0) {
    return undefined
  }

  return { left, top, width, height }
}
